import { findBySearchMask, parseSearchMask } from './SearchMask.js';
import { DEFAULT_NS } from './types.js';

// src/core/imcmd/UiCommandRunner.ts
/**
 * Минимальный раннер: парсит строку и исполняет команды последовательно.
 *
 * Поведение:
 *  - Registry.runToken может вернуть объект, массив объектов или null.
 *  - Результаты плоско склеиваются в один массив.
 *  - Если по всем токенам результатов нет (все null) — возвращается null
 *    (можно использовать для отката в legacy).
 */
class UiCommandRunner {
    constructor(registry) {
        this.registry = registry;
    }
    //** Выполнение команды */
    run(cmdString, ctx, solvedArgs) {
        const tokens = parseCmdString(cmdString);
        return this.executeTokens(tokens, ctx, solvedArgs);
    }
    /**
         * Выполнить ПАКЕТ команд.
         * Поддерживает строку, токен или массив (вплоть до смешанных строк и токенов).
         * Внутри каждая строка разбирается в токены тем же парсером.
         */
    runMany(input, ctx, solvedArgs) {
        if (Array.isArray(input) && input.length === 0)
            return null;
        const arr = Array.isArray(input) ? input : [input];
        const tokens = [];
        for (const it of arr) {
            if (typeof it === 'string') {
                const parsed = parseCmdString(it);
                if (parsed?.length)
                    tokens.push(...parsed);
            }
            else {
                tokens.push(it);
            }
        }
        return this.executeTokens(tokens, ctx, solvedArgs);
    }
    /**
     * Общая реализация исполнения набора токенов:
     *  - merge solvedArgs (приоритет у token.solvedArgs),
     *  - resolve targetItems по targetMask + ctx (viewHost/uiNotMask),
     *  - вызвать registry.runToken для каждого токена,
     *  - сплющить результаты; вернуть null, если пусто.
     */
    executeTokens(tokens, ctx, solvedArgs) {
        const out = [];
        for (const t of tokens) {
            // merge solvedArgs — как и в исходной run: приоритет у t.solvedArgs
            if (solvedArgs) {
                t.solvedArgs = t.solvedArgs
                    ? { ...solvedArgs, ...t.solvedArgs }
                    : { ...solvedArgs };
            }
            // если задана маска — выбираем цели в пределах viewHost (с учётом uiNotMask)
            if (!t.targetItems?.length && t.targetMask?.mask && ctx.viewHost) {
                t.targetItems = findBySearchMask(ctx.viewHost, t.targetMask, ctx.sourceEl, ctx.uiNotMask);
            }
            // делегируем выполнение в реестр провайдеров (стандартный путь)
            const res = this.registry.runToken(t, ctx);
            if (!res)
                continue;
            if (Array.isArray(res)) {
                for (const r of res)
                    out.push(r);
            }
            else {
                out.push(res);
            }
        }
        return out.length === 0 ? null : out;
    }
}
/**
 * Разобрать строку в последовательность токенов.
 *
 * Формат:
 *   "<cmd>; <cmd>; ..."  — команды разделены ';'
 *   где <cmd> обрабатывается с помощью parseSingleCommand
 */
function parseCmdString(cmdString) {
    return cmdString
        .split(';')
        .map(s => s.trim())
        .filter(Boolean)
        .map(parseSingleCommand)
        .filter((t) => t !== undefined);
}
/**
 * Разбор строки одной команды.
 *
 * Поддерживаются варианты:
 *   - "ns::command|arg1|arg2"      — новый синтаксис с namespace через '::', аргументы через '|'
 *   - "command|arg1"               — без namespace, аргументы через '|'
 *   - "command:arg1"               — старый синтаксис, аргументы через ':'
 *   - "@itemKey.command|..."       — адресная форма для конкретного элемента с ns пространства
 *   - "@itemKey.ns::command|..."   — адресная форма для конкретного элемента
 *
 * Правила работы:
 *   - Разделитель аргументов:
 *       • если в строке есть хотя бы один символ '|' или '::' → разделитель '|'
 *       • иначе используется ':'
 *   - Namespace и команда:
 *       • пробуем разделитель '::'
 *       • если разделителя нет — namespace = DEFAULT_NS
 *   - Если команда пустая → возвращается undefined
 *
 * @param raw строка команды
 * @returns объект UiCommandToken с распарсенными полями или undefined при ошибке
 */
function parseSingleCommand(raw) {
    // Нормализация устаревших команд
    raw = normalizeLegacyCommand(raw);
    const argSep = (raw.includes('|') || raw.includes('::')) ? '|' : ':';
    const [head, ...argParts] = raw.split(argSep);
    let args = argParts.map(s => s.trim());
    let namespace;
    let command;
    let notSolvedMask;
    // ── НОВОЕ: групповая адресная форма вида "@(<mask>).ns::command" ────────────
    if (head.startsWith('@(')) {
        const close = findBalancedCloseParen(head, 1 /* индекс '(' после '@' */);
        if (close < 0) {
            throw new Error('[imcmd] Не закрыта скобка в адресной форме:' + raw);
        }
        // внутренняя часть скобок — это mask-выражение (включая ^area)
        notSolvedMask = head.slice(2, close).trim(); // например ".im-table^global"
        // после скобок ожидается ".ns::cmd" или просто ".command"
        let rest = head.slice(close + 1).trim(); // например ".items::reload"
        if (rest.startsWith('.'))
            rest = rest.slice(1);
        command = rest; // "items::reload" → распарсится ниже как ns+cmd
    }
    // Команды обращенные к конкретному элементу @itemKey.command
    // для совместимости поддерживаются аргументы @itemKey.command[.extra...]
    else if (head.startsWith('@')) {
        const parts = head.slice(1).split('.').map(p => p.trim());
        if (parts.length >= 2) {
            const [cmdItemKey, realCommand, ...extra] = parts;
            notSolvedMask = '@' + cmdItemKey;
            if (!realCommand || !cmdItemKey) {
                console.error('[imcmd] Не соответствует формату: ', raw);
                return undefined;
            }
            // Такое может встретиться только в старом синтаксисе и там иных параметров нет по этому можно заменить аргументы
            if (extra?.length > 0)
                args = extra;
            command = realCommand;
        }
        else {
            console.error('[imcmd] Не соответствует формату: ', raw);
            return undefined;
        }
    }
    if (!command)
        command = head.trim();
    // Пробуем определить ns
    if (!namespace) {
        const dblIdx = command.indexOf('::');
        if (dblIdx > 0) {
            namespace = command.slice(0, dblIdx).trim().toLowerCase();
            command = command.slice(dblIdx + 2).trim();
        }
        else {
            namespace = DEFAULT_NS;
        }
    }
    // Пустая строка недопускается
    if (command === '')
        return undefined;
    // Построение маски для notSolvedMask (включая поддержку ^area и tableMode)
    let itemMask;
    if (notSolvedMask) {
        itemMask = parseSearchMask(notSolvedMask, namespace === 'table');
    }
    // Результат распознания
    return { raw, namespace, command, rawArgs: args, targetMask: itemMask, solvedArgs: undefined };
}
/**
 * Преобразование устаревших строк команд к новому синтаксису.
 *
 * Поддерживаемые случаи:
 * - Выбор всех строк в таблице
 *   "checkAllRows:footagesTable" → "@footagesTable.table::checkAll"
 *
 * - Перезагрузка таблицы (устаревшее reloadBody)
 *   "@filesTable.reloadBody" → "@filesTable.table::reload"
 */
function normalizeLegacyCommand(raw) {
    const s = raw?.trim() ?? '';
    if (!s)
        return s;
    if (s.startsWith('@')) {
        const parts = s.slice(1).split('.');
        const [head, arg1] = parts;
        // 2) @tableKey.reloadBody
        if (arg1 === 'reloadBody') {
            return `@${head}.table::reloadAll`;
        }
        return s;
    }
    else {
        const parts = s.split(':').map(p => p.trim());
        if (parts.length >= 2) {
            const [head, arg1, ...rest] = parts;
            // 1) checkAllRows:tableKey
            if (head === 'checkAllRows' && arg1) {
                return `@${arg1}.table::selectAll`;
            }
            // 3) slideToggle:selector:args... → toggle:selector:args...
            if (head === 'slideToggle') {
                return ['toggle', arg1, ...rest].join(':');
            }
            // 4) deleteCheckedItems:[data-key=...]
            if (head === 'deleteCheckedItems' && arg1) {
                return `items::deleteSelected|${arg1}`;
            }
            // 5) uncheckAllItems:[data-key=...]
            if (head === 'uncheckAllItems' && arg1) {
                return `items::unselectAll|${arg1}`;
            }
            // 6) checkAllItems:[data-key=...]
            if (head === 'checkAllItems' && arg1) {
                return `items::selectAll|${arg1}`;
            }
            // 7) checkToggleAllItems:[data-key=...]
            if (head === 'checkToggleAllItems' && arg1) {
                return `items::toggle|${arg1}`;
            }
            // ✅ 8) reload:<itemKey>  →  @<itemKey>.reload
            if (head === 'reload' && parts.length === 2 && arg1) {
                // только буквы / цифры / _
                if (/^[a-zA-Z0-9_]+$/.test(arg1)) {
                    return `@${arg1}.reload`;
                }
            }
        }
    }
    return s;
}
// ── helper: поиск парной ")" с учётом вложенности после "@(" ──────────────
function findBalancedCloseParen(s, openIndex /* индекс '(' */) {
    let depth = 1;
    for (let i = openIndex + 1; i < s.length; i++) {
        const ch = s[i];
        if (ch === '(')
            depth++;
        else if (ch === ')') {
            depth--;
            if (depth === 0)
                return i;
        }
    }
    return -1; // не нашли
}

export { UiCommandRunner, parseCmdString };
